import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { 
  Box, TextField, Button, Typography, CircularProgress, IconButton, InputAdornment 
} from "@mui/material";
import { FaKey } from "react-icons/fa"; // Icon for generating password
import axiosInstance from "../axiosInstance";
import { toast } from "react-toastify";

const apiUrl = process.env.REACT_APP_API_URL;

const VendorViewPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [vendor, setVendor] = useState({
    id: "",
    vendorId: "",
    username: "",
    email: "",
  });

  const [password, setPassword] = useState(""); // Separate state for password
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    fetchVendor();
  }, []);

  const fetchVendor = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");

      if (!token) {
        navigate("/login", { replace: true });
        return;
      }

      const response = await axiosInstance.get(`${apiUrl}/vendor/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.vendor) {
        setVendor({
          id: response.data.vendor.id || "",
          vendorId: response.data.vendor.vendorId || "",
          username: response.data.vendor.username || "",
          email: response.data.vendor.email || "",
        });
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to fetch vendor data");
    } finally {
      setLoading(false);
    }
  };

  const generatePassword = () => {
    const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*";
    let generatedPassword = "";
    for (let i = 0; i < 10; i++) {
      generatedPassword += characters.charAt(Math.floor(Math.random() * characters.length));
    }
    setPassword(generatedPassword);
  };

  const handleChange = (e) => {
    setVendor({ ...vendor, [e.target.name]: e.target.value });
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setErrors({});

    if (!vendor.vendorId || !vendor.username || !vendor.email) {
      setErrors({
        vendorId: !vendor.vendorId ? "Vendor ID is required" : "",
        username: !vendor.username ? "Username is required" : "",
        email: !vendor.email ? "Email is required" : "",
      });
      return;
    }

    try {
      setUpdating(true);
      const token = localStorage.getItem("authToken");

      const updateData = {
        vendorId: vendor.vendorId,
        username: vendor.username,
        email: vendor.email,
      };

      // Include password only if it's changed
      if (password) {
        updateData.password = password;
      }

      const response = await axiosInstance.put(
        `${apiUrl}/vendor/${id}`,
        updateData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(response.data.message || "Vendor updated successfully!");
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update vendor");
    } finally {
      setUpdating(false);
    }
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight="100vh"
      sx={{ bgcolor: "#f7f7f7", p: 3 }}
    >
      <Typography variant="h4" gutterBottom>
        Vendor Details
      </Typography>

      {loading ? (
        <CircularProgress />
      ) : (
        <form onSubmit={handleUpdate}>
          <TextField
            label="Vendor ID"
            variant="outlined"
            margin="normal"
            fullWidth
            name="vendorId"
            value={vendor.vendorId}
            onChange={handleChange}
            error={Boolean(errors.vendorId)}
            helperText={errors.vendorId}
          />
          <TextField
            label="Username"
            variant="outlined"
            margin="normal"
            fullWidth
            name="username"
            value={vendor.username}
            onChange={handleChange}
            error={Boolean(errors.username)}
            helperText={errors.username}
          />
          <TextField
            label="Email"
            variant="outlined"
            margin="normal"
            fullWidth
            name="email"
            value={vendor.email}
            onChange={handleChange}
            error={Boolean(errors.email)}
            helperText={errors.email}
          />
          <TextField
            label="Password"
            type="text"
            variant="outlined"
            margin="normal"
            fullWidth
            name="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={generatePassword} edge="end">
                    <FaKey />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Button
            type="submit"
            variant="contained"
            color="primary"
            size="large"
            fullWidth
            disabled={updating}
            sx={{ mt: 2 }}
          >
            {updating ? <CircularProgress size={24} color="inherit" /> : "Update Vendor"}
          </Button>
        </form>
      )}
    </Box>
  );
};

export default VendorViewPage;
